import React, { useEffect } from 'react';

import { Button, Flex, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';
import { Link, RichTextEditor } from '@mantine/tiptap';
import { IconCalendar, IconTags } from '@tabler/icons-react';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { AnyExtension, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import ReadonlyWorkoutNotesRTE from 'components/workout-program/ReadonlyWorkoutNotesRTE';
import WorkoutProgramEmptyState from 'components/workout-program/WorkoutProgramEmptyState';
import dayjs from 'dayjs';
import useCreateProgram from 'hooks/program/useCreateProgram';
import useUpdateProgram from 'hooks/program/useUpdateProgram';
import { ProgramRetrieve } from 'types';
import notificationHandler from 'utils/notifications';

import { FormValues } from './type';

interface ProgramFormProps {
  program?: ProgramRetrieve | null;
  permission?: boolean;
  handleClose: () => void;
  readonly?: boolean;
}

const EditProgramForm = ({
  program,
  permission = true,
  handleClose,
  readonly = false,
}: ProgramFormProps): JSX.Element => {
  const theme = useMantineTheme();

  const updateProgram = useUpdateProgram();
  const createProgram = useCreateProgram();

  const formValues: FormValues = {
    programId: program?.programId || '',
    date: program?.date || '',
    kiloClassType: program?.kiloClassType.name || '',
    workoutNotes: program?.workoutNotes || '',
  };

  const form = useForm<FormValues>({
    initialValues: formValues,
    validate: {
      workoutNotes: value => (value ? null : 'Workout notes are required'),
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit as AnyExtension,
      Underline as AnyExtension,
      TextAlign.configure({ types: ['paragraph'] }) as AnyExtension,
      Link as AnyExtension,
    ],
    content: form.getInputProps('workoutNotes').value,
    onUpdate: ({ editor: _editor }) => form.getInputProps('workoutNotes').onChange(_editor.getHTML()),
    editable: permission && !readonly,
  });

  const isLoading = updateProgram.isLoading || createProgram.isLoading;

  const handleCloseAndStateReset = (): void => {
    form.reset();
    handleClose();
  };

  const handleProgramUpdateOrCreate = (values: FormValues): void => {
    const { errorNotification, successNotification } = notificationHandler();

    const payload = {
      programId: program?.programId || '',
      workoutNotes: values.workoutNotes,
      date: program?.date || '',
      kiloClassType: program?.kiloClassType.kiloClassTypeId || '',
    };

    if (program?.isProgrammed) {
      updateProgram.mutate(payload, {
        onSuccess: () => {
          successNotification(`${program?.kiloClassType.name} modified successfully`);
          handleCloseAndStateReset();
        },
        onError: error => {
          errorNotification('Updating program failed', error.response?.data.message);
        },
      });
    } else {
      createProgram.mutate(payload, {
        onSuccess: () => {
          successNotification(`${program?.kiloClassType.name} created successfully`);
          handleCloseAndStateReset();
        },
        onError: error => {
          errorNotification('Creating program failed', error.response?.data.message);
        },
      });
    }
  };

  useEffect(() => {
    if (program) {
      form.setValues({
        programId: program.programId,
        date: program.date,
        kiloClassType: program.kiloClassType.name,
        workoutNotes: program.workoutNotes,
      });
      editor?.commands.setContent(program.workoutNotes || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program, editor]);

  const handleSubmit = (values: FormValues): void => {
    handleProgramUpdateOrCreate(values);
  };

  return (
    <form id='edit-program-form' onSubmit={form.onSubmit(handleSubmit)}>
      <Stack justify='center' spacing={theme.spacing.md} py='md'>
        {!readonly && (
          <>
            <Flex gap='md' justify='flex-start' align='center' direction='row' wrap='wrap'>
              <IconCalendar size={theme.fontSizes.xl} />
              <Text fz={theme.fontSizes.xl}>{dayjs(program?.date).format('dddd, MMM D, YYYY')}</Text>
            </Flex>
            <Flex gap='md' justify='flex-start' align='center' direction='row' wrap='wrap'>
              <IconTags size={theme.fontSizes.xl} />
              <Text fz={theme.fontSizes.xl}>{program?.kiloClassType.name}</Text>
            </Flex>
          </>
        )}
        <Group grow>
          {readonly && (
            <>
              {!!form.values.workoutNotes && (
                <ReadonlyWorkoutNotesRTE workoutNotes={form.values.workoutNotes} />
              )}
              {!form.values.workoutNotes && <WorkoutProgramEmptyState />}
            </>
          )}

          {!readonly && (
            <>
              <RichTextEditor editor={editor} {...form.getInputProps('workoutNotes')}>
                <RichTextEditor.Toolbar sticky stickyOffset={60} display={permission ? 'default' : 'none'}>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic data-cy='textEditorInput' />
                    <RichTextEditor.Underline />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Link />
                    <RichTextEditor.Unlink />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.AlignLeft />
                    <RichTextEditor.AlignCenter />
                    <RichTextEditor.AlignRight />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.OrderedList />
                    <RichTextEditor.BulletList />
                  </RichTextEditor.ControlsGroup>
                </RichTextEditor.Toolbar>{' '}
                <RichTextEditor.Content mih={200} h='100%' data-cy='programRte' />
              </RichTextEditor>
            </>
          )}
        </Group>
        {!readonly && (
          <Group position='right' px='sm' py='xs'>
            <Button variant='subtle' onClick={handleCloseAndStateReset}>
              Cancel
            </Button>
            <Button
              data-cy='programSaveBtn'
              form='edit-program-form'
              type='submit'
              color='kiloBlue'
              loading={isLoading}
              disabled={!permission}
            >
              Save
            </Button>
          </Group>
        )}
      </Stack>
    </form>
  );
};

export default EditProgramForm;
